import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, Music2, Mic2, User, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

function Header({ title, sub }) {
  return <div className="mb-8"><h1 className="font-heading text-3xl font-extrabold tracking-tight">{title}</h1><p className="text-slate-400 mt-1">{sub}</p></div>;
}

export function Templates() {
  const nav = useNavigate();
  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get('/templates').then(({ data }) => setPresets(data)).catch(() => {}); }, []);
  return (
    <div>
      <Header title="Templates" sub="One-click presets that make the AI feel intelligent." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {presets.map((p) => (
          <div key={p.id} className="rounded-2xl border border-white/10 bg-[#12141D] p-6 hover:border-[#FF4D00]/40 transition-colors">
            <div className="text-3xl mb-3">{p.emoji}</div>
            <div className="font-heading font-semibold text-lg">{p.name}</div>
            <div className="text-sm text-slate-400 mt-1.5">{p.desc}</div>
            <Button onClick={() => nav('/app/create')} variant="outline" className="rounded-full mt-4 border-white/15 bg-white/5 hover:bg-white/10">Use preset</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const VOICE_LIB = [['Documentary Male', 'onyx'], ['Storyteller', 'fable'], ['Energetic', 'nova'], ['Warm', 'coral'], ['Calm', 'echo'], ['Bright', 'shimmer']];
export function AudioLibrary() {
  return (
    <div>
      <Header title="Audio" sub="Studio voices and music beds available to your projects." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6">
          <div className="flex items-center gap-2 mb-4"><Mic2 className="w-5 h-5 text-[#FF884D]" /><span className="font-heading font-semibold">AI Voices</span></div>
          <div className="grid grid-cols-2 gap-3">
            {VOICE_LIB.map(([label, id]) => (
              <div key={id} className="rounded-xl bg-[#0B0C12] border border-white/10 p-3">
                <div className="text-sm font-medium">{label}</div><div className="font-mono text-[10px] text-slate-500">{id}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6">
          <div className="flex items-center gap-2 mb-4"><Music2 className="w-5 h-5 text-[#8B5CF6]" /><span className="font-heading font-semibold">Music beds</span></div>
          <div className="space-y-2">
            {['Cinematic · Mystery', 'Cinematic · Uplifting', 'Ambient · Calm', 'Lo-fi · Chill'].map((m) => (
              <div key={m} className="rounded-xl bg-[#0B0C12] border border-white/10 p-3 text-sm text-slate-300">{m}</div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">Music beds are applied with automatic ducking inside the Music Studio.</p>
        </div>
      </div>
    </div>
  );
}

export function BrandKit() {
  const [color, setColor] = useState(localStorage.getItem('te_brand_color') || '#FF4D00');
  const [channel, setChannel] = useState(localStorage.getItem('te_brand_channel') || '');
  const save = () => { localStorage.setItem('te_brand_color', color); localStorage.setItem('te_brand_channel', channel); };
  return (
    <div>
      <Header title="Brand Kit" sub="Keep every export on-brand." />
      <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6 max-w-lg space-y-5">
        <div>
          <div className="text-sm text-slate-400 mb-1.5">Channel name</div>
          <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="Lost History" className="w-full rounded-lg bg-[#0B0C12] border border-white/12 px-3 py-2.5 text-sm" />
        </div>
        <div>
          <div className="text-sm text-slate-400 mb-1.5">Accent color</div>
          <div className="flex items-center gap-3">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-12 h-10 rounded-lg bg-transparent border border-white/12" />
            <span className="font-mono text-sm text-slate-300">{color}</span>
          </div>
        </div>
        <Button onClick={save} className="rounded-full"><Palette className="w-4 h-4 mr-1" /> Save brand kit</Button>
      </div>
    </div>
  );
}

export function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div>
      <Header title="Settings" sub="Manage your account." />
      <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6 max-w-lg">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF4D00] to-[#8B5CF6] grid place-items-center text-xl font-bold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-white" />}
          </div>
          <div><div className="font-heading font-semibold text-lg">{user?.name}</div><div className="text-sm text-slate-400">{user?.email}</div></div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#0B0C12] p-4"><div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Plan</div><div className="font-heading font-bold mt-1">{user?.plan_name}</div></div>
          <div className="rounded-xl bg-[#0B0C12] p-4"><div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Minutes left</div><div className="font-heading font-bold mt-1">{Math.max(0, (user?.usage_limit || 0) - (user?.usage_minutes || 0))}</div></div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={() => nav('/app/billing')} className="rounded-full"><Sparkles className="w-4 h-4 mr-1" /> Manage plan</Button>
          <Button onClick={async () => { await logout(); nav('/'); }} variant="outline" className="rounded-full border-white/15 bg-white/5 hover:bg-white/10">Log out</Button>
        </div>
      </div>
    </div>
  );
}
